import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Collapse,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import List from "@mui/material/List";
import { styled } from "@mui/material/styles";
import logo from "../assets/tbp_logo.svg";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AssistantOutlinedIcon from "@mui/icons-material/AssistantOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import TagIcon from "@mui/icons-material/Tag";
import { isMobile } from "react-device-detect";
import { $sidebar } from "../store/sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { $user } from "../store/user";
import { $permissions } from "../store/permissions/permissions";
import bx24 from "./bx24.svg";
import { $bitrix } from "../store/bitrix";
import ThreePOutlinedIcon from "@mui/icons-material/ThreePOutlined";
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import { appRoutes, normalizeRoutePath } from "../routes/appRoutes";

const ListItemButtonSt = styled(ListItemButton)`
  width: 260px;
`;

const menuPath = (route) => `/${route.value}`;

const menuConfig = [
    {
      id: 2,
      label: "Главная",
      path: menuPath(appRoutes.dashboard),
      icon: <HomeOutlinedIcon />,
    },
    {
      id: 6,
      label: "Диалоги",
      icon: <ForumOutlinedIcon />,
      children: [
        {
          path: menuPath(appRoutes.chat),
          label: "Сообщения",
          icon: <SupportAgentIcon />,
        },
        {
          path: menuPath(appRoutes.suggestions),
          label: "Шаблоны",
          icon: <AssistantOutlinedIcon />,
        },
        {
          path: menuPath(appRoutes.chatStats),
          label: "Статистика",
          icon: <QueryStatsOutlinedIcon />,
        },
      ],
    },
    {
      id: 10,
      label: "Чеки заказов",
      path: menuPath(appRoutes.receipts),
      icon: <ReceiptLongOutlinedIcon />,
    },
    {
      id: 9,
      label: "Отзывы",
      path: menuPath(appRoutes.reviews),
      icon: <ThreePOutlinedIcon />,
    },
    {
      id: 3,
      label: "Управление",
      icon: <ManageAccountsOutlinedIcon />,
      children: [
        {
          label: "Пользователи",
          path: menuPath(appRoutes.users),
          icon: <GroupOutlinedIcon />,
        },
        {
          label: "Роли",
          path: menuPath(appRoutes.roles),
          icon: <BadgeOutlinedIcon />,
        },
        {
          label: "Доступы в разделы",
          path: menuPath(appRoutes.permissions),
          icon: <LockOutlinedIcon />,
        },
      ],
    },
    {
      id: 4,
      label: "Отчеты",
      icon: <DonutLargeOutlinedIcon />,
      children: [
        {
          label: "Жалобы покупателей",
          path: menuPath(appRoutes.complaints),
          icon: <AnnouncementOutlinedIcon />,
        },
      ],
    },
    {
      id: 5,
      label: "Товары",
      icon: <CategoryOutlinedIcon />,
      children: [
        {
          path: menuPath(appRoutes.remarks),
          label: "Ошибки в товарах",
          icon: <ErrorOutlineOutlinedIcon />,
        },
        {
          path: menuPath(appRoutes.promo),
          label: "Акционные товары",
          icon: <PercentOutlinedIcon />,
        },
        {
          path: menuPath(appRoutes.tags),
          label: "Рекомендованные",
          icon: <TagIcon />,
        },
        {
          path: menuPath(appRoutes.bundles),
          label: "Бандлы",
          icon: <LocalOfferOutlinedIcon />,
        }
      ],
    },
    {
      id: 11,
      label: "Купоны",
      path: menuPath(appRoutes.coupons),
      icon: <PercentOutlinedIcon />,
    },
    {
      id: 12,
      label: "Быстрые заказы",
      icon: <RocketLaunchOutlinedIcon />,
      children: [
        {
          path: menuPath(appRoutes.fastOrderLinks),
          label: "Создать скидку",
          icon: <AddLinkOutlinedIcon />,
        },
      ],
    },
  ];

export const MenuItems = observer(() => {
  const permissions = $permissions._initPermissions;
  const isDeveloper = $user.isDeveloper;

  const filterItem = useCallback(function filterItem(item) {
    if (item.children) {
      const children = item.children.map(filterItem).filter(Boolean);

      if (!children.length) {
        return null;
      }

      return {
        ...item,
        allowed: true,
        children,
      };
    }

    const routePath = normalizeRoutePath(item.path);
    const allowed = !item.path || isDeveloper || permissions?.[routePath]?.view === 1;

    return allowed
      ? {
          ...item,
          allowed,
        }
      : null;
  }, [permissions, isDeveloper]);

  const menuItems = useMemo(
    () => menuConfig.map(filterItem).filter(Boolean),
    [filterItem]
  );

  const location = useLocation();
  const navigate = useNavigate();

  const [expandedMenuItem, setMenuItem] = useState(null);

  const menuItemClicked = (item) => {
    if (!item.path) {
      setMenuItem(expandedMenuItem === item.id ? null : item.id);
    } else {
      isMobile && $sidebar.hide();
      navigate(item.path);
    }
  };

  const logoutClicked = () => {
    $user.logout().then(() => navigate("/login", { replace: true }));
  };

  //если активная страница находится в дочернем списке, раскрываем список
  useEffect(() => {
    const parent = menuItems.find((el) =>
      el.children?.some((child) => child.path === location.pathname)
    );

    if (parent) {
      setMenuItem(parent.id);
    }
  }, [location.pathname, menuItems]);

  return (
    <>
      <a href="/" style={{ textDecoration: "none" }}>
        <div
          style={{
            display: "flex",
            padding: "11px 16px",
            cursor: "pointer",
            width: "260px",
            borderBottom: "1px solid #e4e4e4",
          }}
        >
          <img
            alt="logo"
            style={{ width: "30px", marginTop: "-3px", marginRight: "10px" }}
            src={logo}
          />
          <div
            style={{
              paddingTop: "2px",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 400,
              color: "#000",
            }}
          >
            ТОВАР БЕЗ ПЕРЕПЛАТ
          </div>
        </div>
      </a>

      <div>
        {menuItems.map(
          (menuItem, key) =>
            menuItem.allowed && (
              <Fragment key={key}>
                <ListItemButtonSt
                  style={menuItem.style}
                  disabled={!menuItem.allowed}
                  key={key}
                  selected={menuItem.path === location.pathname}
                  divider
                  onClick={() => menuItemClicked(menuItem)}
                >
                  <ListItemIcon>{menuItem.icon}</ListItemIcon>
                  <ListItemText primary={menuItem.label} />
                  <div style={{ paddingRight: "14px" }}>
                    <Badge
                      className="badge"
                      color="secondary"
                      badgeContent={menuItem.counter}
                      max={99}
                    />
                  </div>
                  {menuItem.children &&
                    (expandedMenuItem === menuItem.id ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    ))}
                </ListItemButtonSt>
                {menuItem.children && (
                  <Collapse
                    in={expandedMenuItem === menuItem.id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {menuItem.children.map(
                        (subItem, key) =>
                          subItem.allowed && (
                            <ListItemButtonSt
                              key={key}
                              sx={{ pl: 4 }}
                              disabled={!subItem.allowed}
                              divider
                              selected={subItem.path === location.pathname}
                              onClick={() => menuItemClicked(subItem)}
                            >
                              <ListItemIcon>{subItem.icon}</ListItemIcon>
                              <ListItemText primary={subItem.label} />
                              <div style={{ paddingRight: "6px" }}>
                                <Badge
                                  className="badge"
                                  color="secondary"
                                  badgeContent={subItem.counter}
                                  max={99}
                                />
                              </div>
                            </ListItemButtonSt>
                          )
                      )}
                    </List>
                  </Collapse>
                )}
              </Fragment>
            )
        )}
      </div>

      <div style={{ marginTop: "auto" }}>
        <Divider />
        <ListItemButton
          onClick={() => !$bitrix.isAuthenticated && $bitrix.authClicked()}
        >
          <ListItemIcon>
            <img style={{ width: "28px" }} src={bx24} alt="" />
          </ListItemIcon>
          <ListItemText
            primary={
              $bitrix.isAuthenticated
                ? "Битрикс24 подключен"
                : "Подключить Битрикс24"
            }
          />
        </ListItemButton>

        <Divider />
        <ListItemButton onClick={logoutClicked}>
          <ListItemIcon>
            <LogoutOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Выход" />
        </ListItemButton>
      </div>
    </>
  );
});
